import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DataTableComponent, TableColumn, PaginationEvent } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrderShortService } from './order-short.service';
import { OrderShortModel } from './order-short.model';


@Component({
  selector: 'app-order-short',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    SearchFormComponent,
    TranslateModule
  ],
  templateUrl: './order-short.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderShortComponent implements OnInit {
  private orderShortService = inject(OrderShortService);
  private translateService = inject(TranslateService);

  orders: OrderShortModel[] = [];
  selectedOrders: OrderShortModel[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 20;

  // Dropdown options - mapping of values to translation keys
  private channelMap = {
    'Facebook': 'order.short.options.facebook',
    'Shopee': 'order.short.options.shopee',
    'Tmall': 'order.short.options.tmall'
  };
  private storeMap = {
    'Happy Selection Online Store': 'order.short.options.happy_selection_online_store',
    'Colorful Good Goods Warehouse': 'order.short.options.colorful_good_goods_warehouse',
    'Trend Station': 'order.short.options.trend_station'
  };
  private orderSourceMap = {
    'Manual Order': 'order.short.options.manual_order',
    'Copy Order': 'order.short.options.copy_order',
    'Normal Transfer Order': 'order.short.options.normal_transfer_order'
  };
  private giftOptionMap = {
    'Yes': 'order.short.options.yes',
    'No': 'order.short.options.no'
  };

  channels = ['Facebook', 'Shopee', 'Tmall'];
  stores = ['Happy Selection Online Store', 'Colorful Good Goods Warehouse', 'Trend Station'];
  orderSources = ['Manual Order', 'Copy Order', 'Normal Transfer Order'];
  giftOptions = ['Yes', 'No'];

  // Table columns
  columns: TableColumn[] = [
    { label: 'order.short.table.third_party_order_number', prop: 'thirdPartyOrderNumber', width: 150 },
    { label: 'order.short.table.internal_order_number', prop: 'internalOrderNumber', width: 150 },
    { label: 'order.short.table.tags', prop: 'tags', width: 120 },
    { label: 'order.short.table.review_status', prop: 'reviewStatus', width: 120 },
    { label: 'order.short.table.sales_store', prop: 'salesStore', width: 120 },
    { label: 'order.short.table.sales_channel', prop: 'salesChannel', width: 120 },
    { label: 'order.short.table.order_source', prop: 'orderSource', width: 120 },
    { label: 'order.short.table.product_line_count', prop: 'productLineCount', width: 120 },
    { label: 'order.short.table.shortage_count', prop: 'shortageCount', width: 120 },
    { label: 'order.short.table.buyer_nickname', prop: 'buyerNickname', width: 120 },
    { label: 'order.short.table.buyer_notes', prop: 'buyerNotes', width: 150 },
    { label: 'order.short.table.merchant_notes', prop: 'merchantNotes', width: 150 },
    { label: 'order.short.table.receiver_name', prop: 'receiverName', width: 120 },
    { label: 'order.short.table.receiver_phone', prop: 'receiverPhone', width: 150 },
    { label: 'order.short.table.receiver_address', prop: 'receiverAddress', width: 200 },
    { label: 'order.short.table.total_product_amount', prop: 'totalProductAmount', width: 150 },
    { label: 'order.short.table.buyer_payable', prop: 'buyerPayable', width: 120 },
    { label: 'order.short.table.total_product_cost', prop: 'totalProductCost', width: 150 },
    { label: 'order.short.table.third_party_order_time', prop: 'thirdPartyOrderTime', width: 150 },
    { label: 'order.short.table.payment_time', prop: 'paymentTime', width: 150 },
    { label: 'order.short.table.internal_creation_time', prop: 'internalCreationTime', width: 150 },
    { label: 'order.short.table.expected_shipment_time', prop: 'expectedShipmentTime', width: 150 },
    { label: 'order.short.table.suspend_status', prop: 'suspendStatus', width: 120 },
    { label: 'order.short.table.gift_status', prop: 'giftStatus', width: 100 }
  ];

  ngOnInit(): void {
    this.loadOrders();
  }

  loadOrders(): void {
    this.loading = true;
    const searchParams = {
      first: this.first,
      rows: this.rows
    };

    this.orderShortService.getOrders(searchParams).subscribe({
      next: (response) => {
        this.orders = response.data;
        this.totalRecords = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load orders:', error);
        this.loading = false;
      }
    });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.loadOrders();
  }

  onReset(): void {
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    this.first = event.first || 0;
    this.rows = event.rows || 20;
    this.loadOrders();
  }

  onSelectionChange(event: any): void {
    this.selectedOrders = event;
  }


  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Pending Review':
        return 'warning';
      default:
        return 'info';
    }
  }

  getSuspendStatusSeverity(status: string): string {
    switch (status) {
      case 'All Suspend Success':
        return 'success';
      case 'Not Suspended':
        return 'info';
      default:
        return 'warning';
    }
  }

  // Dropdown option getters
  getChannelOptions() {
    return this.channels.map(s => ({
      label: this.translateService.instant(this.channelMap[s as keyof typeof this.channelMap]),
      value: s
    }));
  }

  getStoreOptions() {
    return this.stores.map(s => ({
      label: this.translateService.instant(this.storeMap[s as keyof typeof this.storeMap]),
      value: s
    }));
  }

  getOrderSourceOptions() {
    return this.orderSources.map(s => ({
      label: this.translateService.instant(this.orderSourceMap[s as keyof typeof this.orderSourceMap]),
      value: s
    }));
  }

  getGiftOptions() {
    return this.giftOptions.map(s => ({
      label: this.translateService.instant(this.giftOptionMap[s as keyof typeof this.giftOptionMap]),
      value: s
    }));
  }
} 